
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Action } from "vuex-class"
import { RuleAction, actionSettingsForActionType, actionSetting, ActionSettingField, ActionSetting, FieldSelected, ActionType, DataType, nonAddableFieldTypes, nonRemovableFieldTypes } from "@/types"
import { CaseColumn } from "@/types/caseColumns"

import FieldDropDown from "@/components/Config/Rules/FieldDropDown.vue"
import vSelect from "vue-select"
import ParameterValue from "@/components/Controls/ParameterValue.vue"
import TemplateSelect from "@/components/Config/TemplateSelect.vue"
import HeaderList from "./HeaderList.vue"
import { IntegrationOutOption } from "@/types/integration"
import { cloneDeep, get } from "lodash"
import { fieldTypesExcludeFromRuleSet } from "@/types/dataType"

const setup = namespace("setup")

@Component({
	components: {
		"field-dropdown": FieldDropDown,
		"parameter-value": ParameterValue,
		"template-select": TemplateSelect,
		"v-select": vSelect,
		"header-list": HeaderList
	}
})
export default class RuleActionsSettings extends Vue {
	@Action public loadTeams!: () => void;

	@setup.State("teams") teams!: any[];

	@Prop()
	public setValueFields!: CaseColumn[];

	public fields!: CaseColumn[]

	@Prop({ default: null })
	private action!: RuleAction;

	@Prop({ default: () => [] })
	private integrationOuts!: IntegrationOutOption[]

	private actionSettingsByType = actionSetting.toDictionary("type");
	private actionSettingsById = actionSetting.toDictionary("id");
	private actionSettingField = ActionSettingField;
	private actionType = ActionType;

	private get selectedIntegration (): IntegrationOutOption | null {
		if (!this.action || !this.action.settings || !this.action.settings.integration) {
			return null
		}

		const option = this.integrationOuts.find(o => o.integrationKey === this.action.settings.integration &&
			o.integrationOut === this.action.settings.integrationOut)
		return option || null
	}
	private set selectedIntegration (value: IntegrationOutOption | null) {
		if (!this.action) {
			return
		}

		Vue.set(this.action.settings, "integration", value ? value.integrationKey : null)
		Vue.set(this.action.settings, "integrationOut", value ? value.integrationOut : null)
	}

	private get selectableFields (): CaseColumn[] {
		if (!this.setValueFields || !this.action) return []

		switch (this.action.actionType) {
			case this.actionType.AddField:
				return this.setValueFields.filter(field => !nonAddableFieldTypes.includes(field.dataType))
			case this.actionType.RemoveField:
				return this.setValueFields.filter(field => !nonRemovableFieldTypes.includes(field.dataType))
			default:
				return this.setValueFields.filter(f => !fieldTypesExcludeFromRuleSet.includes(f.dataType))
		}
	}

	private fieldSelected (field: FieldSelected) {
		if (this.action.actionType === this.actionType.SetFieldRequired) {
			this.action.settings["fieldId"] = `${field.field}`
		} else if (this.action.actionType === this.actionType.SetValue) {
			this.action.settings["field"] = `Fields.${field.field}`
			this.action.settings["fieldDataType"] = `${field.primitiveDataType}`
		} else if (this.action.actionType === this.actionType.AddField) {
			this.action.settings["fieldId"] = `${field.field}`
		}
	}

	private showFieldSelector (type: number) {
		if (this.actionSettingField && type === this.actionSettingField.Field) {
			return true
		} else if (!this.actionSettingField) {
			return false
		}
		return type === (this.actionSettingField.FieldId || this.actionSettingField.FieldDataType)
	}

	private teamSelected (team: any) {
		this.action.settings["teamId"] = `${team.id}`
		this.action.settings["teamTitle"] = `${team.title}`
	}

	private mounted () {
	// Initialize the component with the same function used when the action type changes
		this.onActionTypeChanged(this.action.actionType)
		this.loadTeams()
	}

	public get actionTypeSettings (): ActionSetting[] {
		return actionSetting.filter(a => actionSettingsForActionType[this.action.actionType].indexOf(a.type) > -1)
	}

	@Watch("action.actionType")
	private onActionTypeChanged (value: number | null) {
		if (!this.action.settings) {
			this.action.settings = {}
		}

		// Get the fields based on the action type
		const actionSettingFields = actionSettingsForActionType[this.action.actionType] || []

		// Delete values from saved action.settings where action type has changed
		for (var id in this.action.settings) {
			if (!actionSettingFields.includes(this.actionSettingsById[id].type)) {
				delete this.action.settings[id]
			}
		}

		// Create an dictionary to be able to capture the editable field values
		actionSettingFields.forEach(l => {
			const labelName = this.actionSettingsByType[l].id

			if (l === ActionSettingField.DynamicValue) {
				Vue.set(this.action, "dynamic", this.action.settings[labelName]
					? Boolean(this.action.settings[labelName])
					: false)
			}

			if (this.action.settings[labelName] === undefined) {
				Vue.set(this.action.settings, labelName, "")
			}
		})

		this.fields = cloneDeep(this.setValueFields)

		if (this.action.settings["field"]) {
			let fieldData = this.fields.find(f => f.key === this.action.settings["field"])

			if (!fieldData) {
				// Applies to Severity and Status
				fieldData = this.fields.find(f => f.filterKey === this.action.settings["field"])
			}
			Vue.set(this.action, "fieldData", fieldData)
		} else if (this.action.settings["fieldId"]) {
			Vue.set(this.action, "fieldData", this.fields.find(f => f.key === "fields." + this.action.settings["fieldId"]))
		} else {
			Vue.set(this.action, "fieldData", null)
		}
	}

	dynamicValueChanged (isDynamic: boolean) {
		Vue.set(this.action.settings, "newValue", null)
		Vue.set(this.action.settings, "dynamicValue", isDynamic.toString())
		Vue.set(this.action, "dynamic", isDynamic)
	}

	fieldUpdated (field: CaseColumn) {
		Vue.set(this.action.settings, "field", field.filterKey ? field.filterKey : field.key)

		if (field.dynamicField) {
			Vue.set(this.action.settings, "fieldId", field.key.substring(7))
		}

		Vue.set(this.action.settings, "fieldDataType", field.fieldDataType!.toString())
		Vue.set(this.action.settings, "valueFormat", field.dataType)
		Vue.set(this.action.settings, "newValue", null)
	}

	valueUpdated (newLabel: string) {
		Vue.set(this.action.settings, "newValueLabel", newLabel)
	}

	settingUpdated (value: string, settingId: string) {
		Vue.set(this.action.settings, settingId, value)
	}
}
